// Empresa (só administradores): gestão — usuários, setores, políticas, conversas, convites e limites de crédito — /api/client/org*.
// O link do convite aparece UMA vez, ao criar. Uso, saldo e extrato ficam em Relatórios da empresa (CompanyReports, /empresa/relatorios).
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, Navigate } from 'react-router';
import { api, ApiError, brl, when, type OrgChat, type OrgCredits, type OrgInvite, type OrgTranscript, type OrgUsage, type OrgMember, type OrgPolicy, type OrgPool, type OrgSector } from '../api';
import { useMeRequired } from '../me';
import { toast } from '../components/toast';
import { CopyButton } from '../components/CopyButton';
import '../styles/company.css';

type Tab = 'users' | 'sectors' | 'policies' | 'chats' | 'invites' | 'credits';
const TABS: { id: Tab; label: string }[] = [{ id: 'users', label: 'Usuários' }, { id: 'sectors', label: 'Setores' }, { id: 'policies', label: 'Políticas' }, { id: 'chats', label: 'Conversas' }, { id: 'invites', label: 'Convites' }, { id: 'credits', label: 'Limites de crédito' }];
const ROLE: Record<string, string> = { admin: 'administrador', member: 'membro' };
const KIND: Record<string, string> = { topup: 'recarga', charge: 'consumo', adjust: 'ajuste' };
const day = (t: number) => new Date(t).toLocaleDateString('pt-BR');

/** Carrega uma lista da empresa: `.skel` enquanto busca, erro com "Tentar de novo". */
function useLoad<T>(url: string) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(() => { setError(null); setData(null); api<T>(url).then(setData).catch((e) => setError((e as Error).message)); }, [url]);
  useEffect(() => { load(); }, [load]);
  return { data, setData, error, load };
}

function State({ error, load, children, ready }: { error: string | null; load: () => void; children: React.ReactNode; ready: boolean }) {
  if (error) return <div className="empty"><b>Não foi possível carregar</b><p>{error}</p><button type="button" className="btn" onClick={load}>Tentar de novo</button></div>;
  if (!ready) return <div className="co-skel" aria-hidden="true"><div className="skel" /><div className="skel" /><div className="skel" /></div>;
  return <>{children}</>;
}

export function Company() {
  const { me } = useMeRequired();
  const [tab, setTab] = useState<Tab>('users');
  if (!me.org) return <Navigate to="/" replace />;   // conta pessoal
  if (me.org.role !== 'admin') {
    return <div className="ph"><div><span className="eyebrow">Gestão</span><h1>Empresa</h1><div className="empty"><b>Apenas administradores acessam esta página</b><p>Peça a um administrador de {me.org.name}.</p></div></div></div>;
  }
  return (
    <div className="co-page">
      <div className="ph">
        <div>
          <span className="eyebrow">Gestão</span>
          <h1>{me.org.name}</h1>
          <p>Usuários, convites e limites de crédito da empresa. Quem entra por convite passa a usar só a conta da empresa.</p>
        </div>
        <div className="actions"><Link className="btn ghost" to="/empresa/relatorios">Relatórios da empresa</Link></div>
      </div>
      <div className="co-tabs" role="tablist" aria-label="Empresa">
        {TABS.map((t) => (
          <button key={t.id} type="button" role="tab" id={`co-tab-${t.id}`} aria-selected={tab === t.id} aria-controls="co-panel" className={tab === t.id ? 'on' : ''} onClick={() => setTab(t.id)}>{t.label}</button>
        ))}
      </div>
      <div id="co-panel" role="tabpanel" aria-labelledby={`co-tab-${tab}`}>
        {tab === 'users' && <Users selfId={me.clientId} />}
        {tab === 'sectors' && <Sectors />}
        {tab === 'policies' && <Policies />}
        {tab === 'chats' && <ChatsTab />}
        {tab === 'invites' && <Invites />}
        {tab === 'credits' && <Credits />}
      </div>
    </div>
  );
}

function Users({ selfId }: { selfId: string }) {
  const { data, setData, error, load } = useLoad<OrgMember[]>('/api/client/org/members');
  const sectors = useLoad<OrgSector[]>('/api/client/org/sectors').data;   // para a coluna Setor
  const [busy, setBusy] = useState<string | null>(null);
  const setSector = async (m: OrgMember, sectorId: string | null) => {
    setBusy(m.id);
    try {
      await api(`/api/client/org/members/${m.id}/sector`, { method: 'PUT', body: JSON.stringify({ sectorId }) });
      setData((cur) => (cur ?? []).map((x) => (x.id === m.id ? { ...x, sectorId } : x)));
      toast('Setor atualizado');
    } catch (e) { toast((e as Error).message, 'err'); }
    finally { setBusy(null); }
  };
  const patch = async (m: OrgMember, body: { role?: OrgMember['role']; status?: OrgMember['status'] }) => {
    setBusy(m.id);
    try {
      const r = await api<{ id: string; role: OrgMember['role']; status: OrgMember['status'] }>(`/api/client/org/members/${m.id}`, { method: 'PATCH', body: JSON.stringify(body) });
      setData((cur) => (cur ?? []).map((x) => (x.id === m.id ? { ...x, role: r.role, status: r.status } : x)));
      toast('Usuário atualizado');
    } catch (e) { toast((e as Error).message, 'err'); }
    finally { setBusy(null); }
  };
  return (
    <section className="sec" aria-busy={data === null && !error}>
      <State error={error} load={load} ready={data !== null}>
        {data && data.length <= 1 ? <div className="empty"><b>Só você por enquanto</b><p>Convide pessoas na aba Convites.</p></div> : null}
        {data && data.length > 0 && (
          <table className="co-tbl">
            <thead><tr><th>Nome</th><th>E-mail</th><th>Papel</th><th>Setor</th><th>Situação</th><th>Desde</th><th><span className="sr-only">Ações</span></th></tr></thead>
            <tbody>{data.map((m) => (
              <tr key={m.id}>
                <td data-label="Nome"><b>{m.name ?? '—'}</b>{m.id === selfId && <span className="pill"> você</span>}</td>
                <td data-label="E-mail" className="ell" title={m.email}>{m.email}</td>
                <td data-label="Papel">{ROLE[m.role]}</td>
                <td data-label="Setor">
                  <select value={m.sectorId ?? ''} disabled={busy === m.id || !sectors} aria-label={`Setor de ${m.name ?? m.email}`} onChange={(e) => void setSector(m, e.target.value || null)}>
                    <option value="">Sem setor</option>{(sectors ?? []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </td>
                <td data-label="Situação"><span className={m.status === 'active' ? 'ok' : 'err'}>{m.status === 'active' ? 'ativo' : 'desativado'}</span></td>
                <td data-label="Desde" className="mono">{day(m.createdAt)}</td>
                <td className="co-acts">
                  <button type="button" className="btn sm" disabled={busy === m.id} onClick={() => void patch(m, { role: m.role === 'admin' ? 'member' : 'admin' })}>{m.role === 'admin' ? 'Tornar membro' : 'Tornar administrador'}</button>
                  {m.id !== selfId && <button type="button" className={`btn sm ${m.status === 'active' ? 'danger' : ''}`} disabled={busy === m.id} onClick={() => void patch(m, { status: m.status === 'active' ? 'disabled' : 'active' })}>{m.status === 'active' ? 'Desativar' : 'Reativar'}</button>}
                </td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </State>
    </section>
  );
}

function Sectors() {
  const { data, setData, error, load } = useLoad<OrgSector[]>('/api/client/org/sectors');
  const [name, setName] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<{ id: string; name: string } | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);

  const create = async (e: FormEvent) => {
    e.preventDefault();
    setErr(''); setBusy(true);
    try {
      const s = await api<{ id: string; name: string }>('/api/client/org/sectors', { method: 'POST', body: JSON.stringify({ name }) });
      setData((cur) => [...(cur ?? []), { ...s, memberCount: 0 }].sort((a, b) => a.name.localeCompare(b.name)));
      setName('');
    } catch (e2) { setErr((e2 as Error).message); }
    finally { setBusy(false); }
  };
  const rename = async () => {
    if (!editing) return;
    try {
      await api(`/api/client/org/sectors/${editing.id}`, { method: 'PUT', body: JSON.stringify({ name: editing.name }) });
      setData((cur) => (cur ?? []).map((s) => (s.id === editing.id ? { ...s, name: editing.name.replace(/\s+/g, ' ').trim() } : s)));
      setEditing(null);
      toast('Setor renomeado');
    } catch (e) { toast((e as Error).message, 'err'); }
  };
  const remove = async (s: OrgSector) => {
    setConfirming(null);
    try { await api(`/api/client/org/sectors/${s.id}`, { method: 'DELETE' }); }
    catch (e) { if (!(e instanceof ApiError && e.status === 404)) { toast((e as Error).message, 'err'); return; } }   // 409: ainda tem usuários
    setData((cur) => (cur ?? []).filter((x) => x.id !== s.id));
    toast('Setor apagado');
  };
  return (
    <section className="sec">
      <form className="co-form co-form-2" onSubmit={create}>
        <label className="field"><span>Novo setor</span><input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} required placeholder="Ex.: Financeiro" autoComplete="off" /></label>
        <button type="submit" className="btn primary" disabled={busy}>{busy ? 'Criando…' : 'Criar setor'}</button>
        <div className="co-err" role="alert">{err}</div>
      </form>
      <State error={error} load={load} ready={data !== null}>
        {data && data.length === 0 ? <div className="empty"><b>Nenhum setor ainda</b><p>Crie setores e coloque os usuários neles (aba Usuários).</p></div> : (
          <table className="co-tbl">
            <thead><tr><th>Setor</th><th>Usuários</th><th><span className="sr-only">Ações</span></th></tr></thead>
            <tbody>{(data ?? []).map((s) => (
              <tr key={s.id}>
                <td data-label="Setor">{editing?.id === s.id
                  ? <input value={editing.name} autoFocus maxLength={60} aria-label="Novo nome do setor" onChange={(e) => setEditing({ id: s.id, name: e.target.value })} onKeyDown={(e) => { if (e.key === 'Enter') void rename(); if (e.key === 'Escape') setEditing(null); }} />
                  : <b>{s.name}</b>}</td>
                <td data-label="Usuários" className="mono">{s.memberCount}</td>
                <td className="co-acts">
                  {editing?.id === s.id ? (
                    <><button type="button" className="btn sm primary" onClick={() => void rename()}>Salvar</button><button type="button" className="btn sm" onClick={() => setEditing(null)}>Cancelar</button></>
                  ) : confirming === s.id ? (
                    <><button type="button" className="btn sm danger" onClick={() => void remove(s)}>Confirmar</button><button type="button" className="btn sm" autoFocus onClick={() => setConfirming(null)}>Cancelar</button></>
                  ) : (
                    <><button type="button" className="btn sm" onClick={() => setEditing({ id: s.id, name: s.name })}>Renomear</button><button type="button" className="btn sm danger" onClick={() => setConfirming(s.id)}>Apagar</button></>
                  )}
                </td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </State>
    </section>
  );
}

/** Políticas: por setor ou usuário, quais modelos (padrão: sem restrição) e quais pools da empresa. O servidor impõe (submitJob); aqui só se edita. */
function Policies() {
  const { me } = useMeRequired();
  const sectors = useLoad<OrgSector[]>('/api/client/org/sectors');
  const members = useLoad<OrgMember[]>('/api/client/org/members');
  const pools = useLoad<OrgPool[]>('/api/client/org/pools');
  const [kind, setKind] = useState<'sector' | 'user'>('sector');
  const [subject, setSubject] = useState('');
  const [policy, setPolicy] = useState<OrgPolicy | null>(null);
  const [free, setFree] = useState(true);           // interruptor "sem restrição" (models = null)
  const [models, setModels] = useState<string[]>([]);
  const [poolIds, setPoolIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const subjects = kind === 'sector' ? (sectors.data ?? []).map((s) => ({ id: s.id, label: s.name })) : (members.data ?? []).map((m) => ({ id: m.id, label: m.name ?? m.email }));
  useEffect(() => { setSubject(''); setPolicy(null); }, [kind]);
  useEffect(() => {
    if (!subject) { setPolicy(null); return; }
    setError(null); setPolicy(null);
    api<OrgPolicy>(`/api/client/org/policy?kind=${kind}&id=${encodeURIComponent(subject)}`)
      .then((p) => { setPolicy(p); setFree(p.models === null); setModels(p.models ?? []); setPoolIds(p.pools); })
      .catch((e) => setError((e as Error).message));
  }, [kind, subject]);

  const known = [...new Set([...me.models, ...me.openrouter.map((o) => o.id), ...models])].sort();
  const flip = (list: string[], id: string) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
  const save = async () => {
    setSaving(true);
    try {
      const p = await api<OrgPolicy>('/api/client/org/policy', { method: 'PUT', body: JSON.stringify({ kind, id: subject, models: free || models.length === 0 ? null : models, pools: poolIds }) });
      setPolicy(p); setFree(p.models === null); setModels(p.models ?? []); setPoolIds(p.pools);
      toast('Política salva');
    } catch (e) { toast((e as Error).message, 'err'); }
    finally { setSaving(false); }
  };
  const loadErr = sectors.error ?? members.error ?? pools.error;
  return (
    <section className="sec">
      <State error={loadErr} load={() => { sectors.load(); members.load(); pools.load(); }} ready={sectors.data !== null && members.data !== null && pools.data !== null}>
        <div className="co-form co-form-2">
          <label className="field"><span>Aplicar a</span>
            <select value={kind} onChange={(e) => setKind(e.target.value as 'sector' | 'user')}><option value="sector">Setor</option><option value="user">Usuário</option></select>
          </label>
          <label className="field"><span>{kind === 'sector' ? 'Setor' : 'Usuário'}</span>
            <select value={subject} onChange={(e) => setSubject(e.target.value)} aria-label="Sujeito da política">
              <option value="">Escolha…</option>{subjects.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </label>
        </div>
        {kind === 'sector' && sectors.data?.length === 0 && <div className="empty"><b>Nenhum setor ainda</b><p>Crie um setor na aba Setores ou aplique a política a um usuário.</p></div>}
        {error && <div className="empty"><b>Não foi possível carregar</b><p>{error}</p></div>}
        {subject && !error && !policy && <div className="co-skel" aria-hidden="true"><div className="skel" /><div className="skel" /></div>}
        {policy && (
          <div className="co-policy">
            <div className="co-box">
              <div className="sec-h"><h2>Modelos</h2></div>
              <label className="co-check"><input type="checkbox" checked={free} onChange={(e) => setFree(e.target.checked)} /><span>Sem restrição (todos os modelos)</span></label>
              {!free && (
                <div className="co-checks" role="group" aria-label="Modelos permitidos">
                  {known.length === 0 ? <p className="mute">Nenhum modelo disponível agora.</p> : known.map((id) => (
                    <label key={id} className="co-check"><input type="checkbox" checked={models.includes(id)} onChange={() => setModels((l) => flip(l, id))} /><span className="mono">{id}</span></label>
                  ))}
                </div>
              )}
              <p className="co-note">Com lista, vale a união da lista do usuário e da do setor. Lista vazia = sem restrição.</p>
            </div>
            <div className="co-box">
              <div className="sec-h"><h2>Pools da empresa</h2></div>
              {pools.data && pools.data.length === 0 ? (
                <div className="empty"><b>Nenhuma pool da empresa</b><p>As pools são ativadas pela Clusterizze por contrato.</p></div>
              ) : (
                <div className="co-checks" role="group" aria-label="Pools permitidas">
                  {(pools.data ?? []).map((p) => (
                    <label key={p.id} className="co-check"><input type="checkbox" checked={poolIds.includes(p.id)} onChange={() => setPoolIds((l) => flip(l, p.id))} /><span>{p.name} <em className="mute">· {p.workers} {p.workers === 1 ? 'máquina' : 'máquinas'}</em></span></label>
                  ))}
                </div>
              )}
              <p className="co-note">Sem concessão, o usuário usa só a Community. Administradores usam todas as pools da empresa.</p>
            </div>
            <button type="button" className="btn primary" onClick={() => void save()} disabled={saving}>{saving ? 'Salvando…' : 'Salvar política'}</button>
          </div>
        )}
      </State>
    </section>
  );
}

const DAY = 86400e3;
const RANGES: { id: string; label: string }[] = [{ id: '7', label: 'Últimos 7 dias' }, { id: '30', label: 'Últimos 30 dias' }, { id: 'month', label: 'Mês atual' }];
const GROUPS: { id: 'user' | 'sector' | 'model'; label: string }[] = [{ id: 'user', label: 'Usuário' }, { id: 'sector', label: 'Setor' }, { id: 'model', label: 'Modelo' }];

/** Relatório de uso (F15): metadados de `jobs` por usuário, setor ou modelo, no período. Sem texto de conversa. */
function UsageTab() {
  const [range, setRange] = useState('30');
  const [groupBy, setGroupBy] = useState<'user' | 'sector' | 'model'>('user');
  const [data, setData] = useState<OrgUsage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(() => {
    setError(null); setData(null);
    const now = Date.now(), d = new Date();
    const from = range === 'month' ? new Date(d.getFullYear(), d.getMonth(), 1).getTime() : now - Number(range) * DAY;
    api<OrgUsage>(`/api/client/org/usage?groupBy=${groupBy}&from=${encodeURIComponent(new Date(from).toISOString())}&to=${encodeURIComponent(new Date(now).toISOString())}`).then(setData).catch((e) => setError((e as Error).message));
  }, [range, groupBy]);
  useEffect(() => { load(); }, [load]);
  return (
    <section className="sec" aria-busy={data === null && !error}>
      <div className="co-form co-form-2">
        <label className="field"><span>Período</span><select value={range} onChange={(e) => setRange(e.target.value)}>{RANGES.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}</select></label>
        <label className="field"><span>Agrupar por</span><select value={groupBy} onChange={(e) => setGroupBy(e.target.value as typeof groupBy)}>{GROUPS.map((g) => <option key={g.id} value={g.id}>{g.label}</option>)}</select></label>
      </div>
      <State error={error} load={load} ready={data !== null}>
        {data && data.rows.length === 0 ? <div className="empty"><b>Sem consumo no período</b></div> : data && (
          <table className="co-tbl">
            <thead><tr><th>{GROUPS.find((g) => g.id === groupBy)?.label}</th><th className="r">Tarefas</th><th className="r">Tokens de entrada</th><th className="r">Tokens de saída</th><th className="r">Custo</th></tr></thead>
            <tbody>{data.rows.map((r) => (
              <tr key={r.key || r.name}><td data-label={GROUPS.find((g) => g.id === groupBy)?.label}><b>{r.name}</b></td><td data-label="Tarefas" className="mono r">{r.jobs}</td><td data-label="Entrada" className="mono r">{r.inputTokens}</td><td data-label="Saída" className="mono r">{r.outputTokens}</td><td data-label="Custo" className="mono r">{brl(r.charge, 2)}</td></tr>
            ))}</tbody>
            <tfoot><tr><td data-label="Total"><b>Total</b></td><td data-label="Tarefas" className="mono r">{data.total.jobs}</td><td data-label="Entrada" className="mono r">{data.total.inputTokens}</td><td data-label="Saída" className="mono r">{data.total.outputTokens}</td><td data-label="Custo" className="mono r">{brl(data.total.charge, 2)}</td></tr></tfoot>
          </table>
        )}
      </State>
    </section>
  );
}

/** Conversas dos membros (F15): leitura SOMENTE da transcrição; cada leitura é registrada (LGPD) e a faixa diz quem leu e quando. */
function ChatsTab() {
  const members = useLoad<OrgMember[]>('/api/client/org/members');
  const [memberId, setMemberId] = useState('');
  const [chats, setChats] = useState<OrgChat[] | null>(null);
  const [open, setOpen] = useState<OrgTranscript | null>(null);
  const [readAt, setReadAt] = useState<{ admin: string; at: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { me } = useMeRequired();
  const choose = (id: string) => {
    setMemberId(id); setChats(null); setOpen(null); setError(null);
    if (!id) return;
    api<OrgChat[]>(`/api/client/org/members/${id}/chats`).then(setChats).catch((e) => setError((e as Error).message));
  };
  const read = (c: OrgChat) => {
    setError(null);
    api<OrgTranscript>(`/api/client/org/members/${memberId}/chats/${c.id}`).then((t) => { setOpen(t); setReadAt({ admin: me.name ?? me.email, at: Date.now() }); }).catch((e) => setError((e as Error).message));
  };
  return (
    <section className="sec">
      <p className="co-note" style={{ margin: '0 0 12px' }}>Somente leitura. Cada leitura fica registrada e o membro pode ver quem leu as conversas dele (Configurações).</p>
      <State error={members.error} load={members.load} ready={members.data !== null}>
        <label className="field" style={{ maxWidth: 360 }}><span>Membro</span>
          <select value={memberId} onChange={(e) => choose(e.target.value)} aria-label="Membro"><option value="">Escolha…</option>{(members.data ?? []).map((m) => <option key={m.id} value={m.id}>{m.name ?? m.email}{m.status === 'disabled' ? ' (desativado)' : ''}</option>)}</select>
        </label>
        {error && <div className="empty"><b>Não foi possível carregar</b><p>{error}</p></div>}
        {memberId && !error && chats === null && <div className="co-skel" aria-hidden="true"><div className="skel" /><div className="skel" /></div>}
        {chats && chats.length === 0 && <div className="empty"><b>Nenhuma conversa</b></div>}
        {chats && chats.length > 0 && !open && (
          <table className="co-tbl">
            <thead><tr><th>Conversa</th><th>Atualizada</th><th><span className="sr-only">Abrir</span></th></tr></thead>
            <tbody>{chats.map((c) => (
              <tr key={c.id}>
                <td data-label="Conversa" className="ell"><b>{c.title ?? 'Sem título'}</b>{c.deletedByMemberAt && <span className="pill"> apagada pelo membro em {when(c.deletedByMemberAt)}</span>}</td>
                <td data-label="Atualizada" className="mono">{when(c.updatedAt)}</td>
                <td className="co-acts"><button type="button" className="btn sm" onClick={() => read(c)}>Ler</button></td>
              </tr>
            ))}</tbody>
          </table>
        )}
        {open && readAt && (
          <div className="co-read">
            <div className="co-read-bar" role="status">Leitura registrada: {readAt.admin} em {when(readAt.at)}</div>
            <div className="co-read-head"><b>{open.title ?? 'Sem título'}</b>{open.deletedByMemberAt && <span className="pill">apagada pelo membro em {when(open.deletedByMemberAt)}</span>}<button type="button" className="btn sm" onClick={() => setOpen(null)}>Voltar à lista</button></div>
            <div className="co-msgs">{open.messages.map((m, i) => (
              <div key={i} className={`co-msg ${m.role}`}><span className="co-who">{m.role === 'user' ? 'Membro' : 'Assistente'} · {when(m.createdAt)}</span><p>{m.content}{m.hasImages ? ' [imagem anexada]' : ''}</p></div>
            ))}</div>
          </div>
        )}
      </State>
    </section>
  );
}

function Invites() {
  const { data, setData, error, load } = useLoad<OrgInvite[]>('/api/client/org/invites');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'member' | 'admin'>('member');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [fresh, setFresh] = useState<{ email: string; link: string } | null>(null);   // o link só existe aqui, até a pessoa sair da aba

  const create = async (e: FormEvent) => {
    e.preventDefault();
    setErr(''); setBusy(true);
    try {
      const r = await api<OrgInvite & { link: string }>('/api/client/org/invites', { method: 'POST', body: JSON.stringify({ email, role }) });
      setFresh({ email: r.email, link: r.link });
      setData((cur) => [{ id: r.id, email: r.email, role: r.role, expiresAt: r.expiresAt, createdAt: Date.now() }, ...(cur ?? [])]);
      setEmail('');
    } catch (e2) { setErr((e2 as Error).message); }
    finally { setBusy(false); }
  };
  const revoke = async (i: OrgInvite) => {
    try { await api(`/api/client/org/invites/${i.id}`, { method: 'DELETE' }); }
    catch (e) { if (!(e instanceof ApiError && e.status === 404)) { toast((e as Error).message, 'err'); return; } }
    setData((cur) => (cur ?? []).filter((x) => x.id !== i.id));
    toast('Convite revogado');
  };
  return (
    <section className="sec">
      <form className="co-form" onSubmit={create}>
        <label className="field"><span>E-mail</span><input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="pessoa@empresa.com" autoComplete="off" /></label>
        <label className="field"><span>Papel</span>
          <select value={role} onChange={(e) => setRole(e.target.value as 'member' | 'admin')}><option value="member">Membro</option><option value="admin">Administrador</option></select>
        </label>
        <button type="submit" className="btn primary" disabled={busy}>{busy ? 'Criando…' : 'Criar convite'}</button>
        <div className="co-err" role="alert">{err}</div>
      </form>
      {fresh && (
        <div className="co-link" role="status">
          <b>Convite para {fresh.email}</b>
          <p>Copie e envie o link agora: ele não é mostrado de novo. Vale por 7 dias e só pode ser usado uma vez.</p>
          <code className="mono">{fresh.link}</code>
          <div className="co-link-acts"><CopyButton text={fresh.link} label="Copiar link do convite" /><button type="button" className="btn sm" onClick={() => setFresh(null)}>Ocultar</button></div>
        </div>
      )}
      <div className="sec-h"><h2>Pendentes</h2>{data && data.length > 0 && <span className="n">{data.length}</span>}</div>
      <State error={error} load={load} ready={data !== null}>
        {data && data.length === 0 ? <div className="empty"><b>Nenhum convite pendente</b></div> : (
          <table className="co-tbl">
            <thead><tr><th>E-mail</th><th>Papel</th><th>Expira em</th><th><span className="sr-only">Ações</span></th></tr></thead>
            <tbody>{(data ?? []).map((i) => (
              <tr key={i.id}>
                <td data-label="E-mail" className="ell" title={i.email}>{i.email}</td>
                <td data-label="Papel">{ROLE[i.role]}</td>
                <td data-label="Expira em" className="mono">{day(i.expiresAt)}</td>
                <td className="co-acts"><button type="button" className="btn sm danger" onClick={() => void revoke(i)}>Revogar</button></td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </State>
    </section>
  );
}

function Credits() {
  const { data, setData, error, load } = useLoad<OrgCredits>('/api/client/org/credits');
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const save = async (id: string) => {
    const raw = (draft[id] ?? '').trim().replace(',', '.');
    const limit = raw === '' ? null : Number(raw);
    if (limit !== null && (!Number.isFinite(limit) || limit < 0)) { toast('Limite inválido', 'err'); return; }
    setBusy(id);
    try {
      await api(`/api/client/org/members/${id}/limit`, { method: 'PUT', body: JSON.stringify({ limit }) });
      setData((cur) => (cur ? { ...cur, members: cur.members.map((m) => (m.id === id ? { ...m, limit } : m)) } : cur));
      setDraft((d) => { const { [id]: _, ...rest } = d; return rest; });
      toast(limit === null ? 'Limite removido' : 'Limite salvo');
    } catch (e) { toast((e as Error).message, 'err'); }
    finally { setBusy(null); }
  };
  return (
    <section className="sec" aria-busy={data === null && !error}>
      <State error={error} load={load} ready={data !== null}>
        {data && (
          <>
            <p className="co-note">A recarga da carteira é feita pelo operador da plataforma. O limite é um teto mensal por usuário sobre a carteira da empresa; vazio = sem limite.</p>
            <div className="sec-h"><h2>Limite mensal por usuário</h2></div>
            <table className="co-tbl">
              <thead><tr><th>Usuário</th><th>Limite (R$)</th><th>Consumo no mês</th><th><span className="sr-only">Salvar</span></th></tr></thead>
              <tbody>{data.members.map((m) => {
                const shown = draft[m.id] ?? (m.limit === null ? '' : String(m.limit));
                return (
                  <tr key={m.id}>
                    <td data-label="Usuário"><b>{m.name ?? m.email}</b></td>
                    <td data-label="Limite (R$)"><input inputMode="decimal" value={shown} onChange={(e) => setDraft((d) => ({ ...d, [m.id]: e.target.value }))} placeholder="sem limite" aria-label={`Limite mensal de ${m.name ?? m.email}`} /></td>
                    <td data-label="Consumo no mês"><span className="mono">{brl(m.spent, 2)}</span></td>
                    <td className="co-acts"><button type="button" className="btn sm" disabled={busy === m.id || draft[m.id] === undefined} onClick={() => void save(m.id)}>Salvar</button></td>
                  </tr>
                );
              })}</tbody>
            </table>
          </>
        )}
      </State>
    </section>
  );
}

/** Relatório da carteira: saldo, consumo do mês e extrato da empresa. */
function CreditsReport() {
  const { data, error, load } = useLoad<OrgCredits>('/api/client/org/credits');
  return (
    <section className="sec" aria-busy={data === null && !error}>
      <State error={error} load={load} ready={data !== null}>
        {data && (
          <>
            <div className="grid g2">
              <div className="card stat"><span className="lbl">Saldo da empresa</span><span className={`val ${data.balance > 0 ? 'ok' : 'err'}`}>{brl(data.balance, 2)}</span>
                <span className="sub">{data.balance > 0 ? 'todos os usuários gastam desta carteira' : 'sem créditos: ninguém consegue usar o chat'}</span></div>
              <div className="card stat"><span className="lbl">Mês de {new Date(data.period.start).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}</span><span className="val">{brl(data.members.reduce((a, m) => a + m.spent, 0), 2)}</span><span className="sub">gasto no mês por todos</span></div>
            </div>
            <div className="sec-h" style={{ marginTop: 22 }}><h2>Extrato da empresa</h2></div>
            {data.ledger.length === 0 ? <div className="empty"><b>Sem movimentações</b></div> : (
              <table className="co-tbl">
                <thead><tr><th>Quando</th><th>Tipo</th><th>Usuário</th><th className="r">Valor</th><th className="r">Saldo após</th></tr></thead>
                <tbody>{data.ledger.map((t) => (
                  <tr key={t.id}>
                    <td data-label="Quando" className="mono">{when(t.createdAt)}</td><td data-label="Tipo">{KIND[t.kind] ?? t.kind}</td>
                    <td data-label="Usuário" className="ell">{t.clientName ?? '—'}</td>
                    <td data-label="Valor" className={`mono r ${t.amount >= 0 ? 'ok' : ''}`}>{brl(t.amount)}</td><td data-label="Saldo após" className="mono r">{brl(t.balanceAfter)}</td>
                  </tr>
                ))}</tbody>
              </table>
            )}
          </>
        )}
      </State>
    </section>
  );
}

/** Relatórios da empresa (só administradores): saldo e extrato da carteira e o uso por usuário, setor ou modelo. */
export function CompanyReports() {
  const { me } = useMeRequired();
  if (!me.org) return <Navigate to="/" replace />;
  if (me.org.role !== 'admin') return <div className="ph"><div><span className="eyebrow">Relatórios</span><h1>Empresa</h1><div className="empty"><b>Apenas administradores acessam esta página</b></div></div></div>;
  return (
    <div className="co-page">
      <div className="ph">
        <div><span className="eyebrow">Relatórios</span><h1>{me.org.name}</h1><p>Saldo e extrato da carteira da empresa e o consumo por usuário, setor ou modelo.</p></div>
        <div className="actions"><Link className="btn ghost" to="/empresa">Gestão da empresa</Link></div>
      </div>
      <CreditsReport />
      <div className="sec-h" style={{ marginTop: 22 }}><h2>Uso no período</h2></div>
      <UsageTab />
    </div>
  );
}
